/**
 * components/BusinessLinkModal.js
 * Search-and-link picker for tying contacts to the same real firm — Contact model redesign,
 * business-linking UI. Modeled on `ItemPickerModal` (multi-select search picker) + `SiblingEditor`
 * (staged-then-confirmed linking) since both already solve this exact shape for a different
 * collection.
 *
 * **One component, three explicit, named shapes — `mode` is a required prop
 * (`"create"|"addMember"|"link"`), never inferred from which other props happen to be passed.**
 * A missing/unrecognized `mode` (or `mode==="addMember"` with no `fixedBusiness`) fails LOUDLY —
 * `console.error` plus a visibly broken render (an error box, never the normal picker UI) — so a
 * caller that forgets it can't silently fall back to some default shape and misbehave quietly.
 *
 *   - **`"link"`** (Kontak's own "+ Tautkan ke Bisnis" and its suggestion-banner "Tautkan") — the
 *     ORIGINAL shape, unchanged: an anchor `contact` (a real, non-internal Contact) is being tied
 *     to something, but the user genuinely doesn't yet know whether that's an EXISTING firm or a
 *     brand-new one — so BOTH sections render. **BISNIS** — every `data.businesses` entry except
 *     `contact`'s own current one (if any), each showing how many contact entries it already
 *     holds (`utils/businesses.js`'s `businessRefCount`). **KONTAK** — every non-internal contact
 *     except `contact` itself, still searching BOTH lists (Pusat/Cabang) at once regardless of
 *     the host page's active tab. A contact already tied to SOME firm is still SHOWN (batch 2f —
 *     manual-testing fix: silently hiding it read as "the contact I'm looking for simply isn't
 *     there, with no explanation") but greyed and unpickable, labeled with the firm it already
 *     belongs to — the way to reach that firm is to tick its own BISNIS row directly, not to pick
 *     one of its members. Both sections filter on the SAME typed query.
 *   - **`"create"`** (Bisnis page's "+ Buat Bisnis Baru") — no anchor at all. The BISNIS section
 *     is NOT rendered (there's no "join an existing firm" affordance from a blank start — that's
 *     what ticking a KONTAK-section member of one already does, via the create-only guard below).
 *     KONTAK is full and tickable, minimum TWO real contacts before "Tautkan" enables, then the
 *     same name step as `"link"`.
 *   - **`"addMember"`** (Bisnis page's "+ Tambah Anggota") — the firm is FIXED, passed as a full
 *     object via `fixedBusiness` (mirrors `contact` being a full object, not an id needing a
 *     second lookup) and rendered as a plain heading, never a tickable row — the BISNIS section
 *     is NOT rendered here either, since there's nothing to pick among (the firm is already
 *     decided). KONTAK is full and tickable, minimum ONE. No name step — confirming always joins
 *     `fixedBusiness` directly.
 *
 * **The anchor `contact` gets its own heading line in `"link"` mode, the same way `fixedBusiness`
 * already does in `"addMember"` (batch 4 — manual-testing report).** Before this, `contact` was
 * folded into `contactIds` on confirm exactly like it always has been, but had NO visible
 * representation anywhere in the picker itself — not a chip (it was never in `selectedContacts`,
 * so the "Dipilih" chip row, below, never rendered it), not a heading, nothing. The screen showed
 * the tickable firm as a chip and the tickable contacts as chips, but silently dropped the ONE
 * participant that was never a choice at all — a real asymmetry with `"addMember"`, which already
 * pulls its own fixed participant (`fixedBusiness`) OUT of the chip row into a heading rather than
 * leaving it invisible. `"link"`'s own heading text is deliberately NOT a copy of `"addMember"`'s
 * ("Menambahkan anggota ke: {firm}") — that names a known DESTINATION; `"link"`'s own destination
 * is exactly what's still undecided at this step, so its heading instead names the one thing that
 * IS already fixed: which contact this is ("Menautkan kontak ini: {nama}"). No new chip variant,
 * no removal control for it — same as `fixedBusiness`, it was never a selection, so there is
 * nothing to un-select.
 *
 * **Two follow-up fixes, `"link"`-only (batch 5 — manual-testing report)**: (1) the intro
 * sentence directly above ALSO named the anchor in bold ("...yang sama dengan **{nama}**"),
 * three lines from the new heading — the same fact stated twice on one screen. The intro no
 * longer names the anchor at all; only the heading does. `"addMember"`'s own intro/heading pair
 * still both name `fixedBusiness` (a real, separately-noted, pre-existing overlap of the same
 * shape) — deliberately UNTOUCHED here, since only `"link"`'s copy was in scope for this fix.
 * (2) the heading now renders ABOVE the "Dipilih" chip row, not below it — a reader used to see
 * what they'd ticked before seeing which contact this whole picker was even opened from.
 * `"addMember"`'s own heading is a SEPARATE JSX block and keeps its own original position (after
 * the chip row) — moving `"link"`'s did not, and must not, move it too.
 *
 * **Two more `"addMember"`-only fixes (batch 6 — manual-testing report), closing the two things
 * batch 5 deliberately left open for it.** (1) The intro/heading overlap batch 5 found but didn't
 * touch — intro "Pilih kontak untuk ditambahkan ke {firm}.", heading "Menambahkan anggota ke:
 * {firm}." right below it — is now fixed the same way `"link"`'s was: the intro no longer names
 * `fixedBusiness` at all ("Pilih kontak untuk ditambahkan."); only the heading does. (Its own
 * position is UNCHANGED — still after the chip row, per batch 5's own explicit "don't move it"
 * instruction; only the wording changed here, not the placement.) (2) The search placeholder
 * ("Cari nama bisnis, nama kontak, atau nomor telepon…") named a BISNIS search that doesn't exist
 * in this mode — no BISNIS section ever renders here, so typing can only ever filter `"addMember"`
 * one's own KONTAK list. **`"create"` had the IDENTICAL mismatch** (its own BISNIS section is
 * equally absent) — reported alongside this fix and, once its own go-ahead came (batch 7),
 * corrected the same way: the placeholder is now `mode === "link" ? "...nama bisnis..." :
 * "Cari nama kontak atau nomor telepon…"`, so `"addMember"` and `"create"` share the identical
 * contacts-only text and only `"link"` (the one mode with a real BISNIS section) keeps the
 * business-inclusive one — one ternary, not three independent strings that could drift apart.
 *
 * **`initialSelectedContactIds`/`initialSelectedBusinessIds` are `"link"`-only.** They exist so
 * Kontak's own suggestion banner can preselect a specific loose contact or firm before the user
 * has ticked anything by hand. `"create"` and `"addMember"` deliberately accept neither — not
 * "available but unused," genuinely absent from both call sites (`pages/BisnisPage.js`) — because
 * an unused prop on one mode/shape is exactly how the three shapes drift apart from each other
 * over time. This also happens to close a real (if previously latent) gap: the ONE way an
 * already-linked contact could ever reach `selectedContacts` is via this seed bypassing
 * `toggleContact`'s own guard (below) — with the seed gone from `"create"`/`"addMember"`, the
 * multi-business conflict guard becomes not just unreached-in-practice but STRUCTURALLY
 * unreachable from either mode (`toggleContact` refuses a click on an already-linked contact
 * unconditionally, and there is no other path into the selection set).
 *
 * The app never decides two entries are the same firm on its own — this component only stages a
 * selection (contacts and, in `"link"` mode, firms) and reports it via `onConfirm`; the actual
 * `businessId` write happens in the caller (`App.js`'s `handleLinkContacts`). On confirm, every
 * business id "in play" is collected — `contact`'s own current one (if any, `"link"` only),
 * `fixedBusiness`'s own id (unconditionally, `"addMember"` only), any ticked BISNIS row (`"link"`
 * only — there is no BISNIS row in the other two modes), and (defense in depth only; an
 * unpickable KONTAK row can never be ticked, so this should always be empty in practice) any
 * ticked contact's own `businessId`:
 *   - **zero** distinct ids (`"create"`/`"link"` only — never `"addMember"`, since
 *     `fixedBusiness.id` is always in the set there): nobody involved has a business yet — the
 *     name is the user's to state (`step === "name"`), never guessed from any contact's or firm's
 *     own name.
 *   - **one**: that's the business everyone joins (a ticked firm needs no name step at all; a
 *     ticked firm plus loose contacts all join that same firm together; `"addMember"` always
 *     lands exactly here, since `fixedBusiness.id` alone is already one real id and nothing else
 *     can add a second, per the structural-unreachability note above).
 *   - **two or more** (`"link"` only in practice — see above for why `"create"`/`"addMember"`
 *     can't reach this): stops here and says so — never silently merges two real businesses. The
 *     caller's own write path (`App.js`'s `handleLinkContacts`) re-checks a related but distinct
 *     thing (defense in depth), but the point is to never even attempt it from here. **The
 *     conflict message is mode-aware** — `"link"`'s wording assumes the user ticked more than one
 *     firm (true there, since BISNIS is tickable); `"addMember"`'s own wording never says that
 *     (the firm isn't tickable in this mode — it names the fixed firm and points at a ticked
 *     CONTACT as the real cause instead, the only way this could ever fire there).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { displayContact } from "../utils/contacts";
import { displayBusiness, businessRefCount } from "../utils/businesses";
import { normalizePhone } from "../utils/contactMatch";
import { useModalEsc, backdropClose } from "../utils/modalStack";

const LIST_LABEL = { PUSAT: "Pusat", CABANG: "Cabang" };
const RENDER_CAP = 100; // mirrors ItemPickerModal's own real-catalog-scale precedent
const VALID_MODES = ["create", "addMember", "link"];

export default function BusinessLinkModal({
  mode, fixedBusiness = null, contact = null, contacts = [], businesses = [],
  initialSelectedContactIds = [], initialSelectedBusinessIds = [],
  error = null, onConfirm, onClose,
}) {
  const [query, setQuery] = useState("");
  const [selectedContacts, setSelectedContacts] = useState(() => new Set(initialSelectedContactIds));
  const [selectedBusinesses, setSelectedBusinesses] = useState(() => new Set(initialSelectedBusinessIds));
  const [step, setStep] = useState("pick"); // "pick" | "name"
  const [businessName, setBusinessName] = useState("");
  const [conflictError, setConflictError] = useState(null);
  const sectionsRef = useRef(null);

  useModalEsc(onClose);

  // Loud-failure guard for a programmer-facing precondition (which mode this is, and — for
  // "addMember" — which firm it's fixed to), never a silent fallback. This is a caller's own
  // contract violation, not user input a form already validated, so it's logged distinctly from
  // every real validation message elsewhere in this component (those are shown inline to the
  // END USER; this is a `console.error` aimed at whoever's wiring a new call site).
  const isValidMode = VALID_MODES.includes(mode);
  const isValidProps = isValidMode && (mode !== "addMember" || !!fixedBusiness);
  useEffect(() => {
    if (!isValidMode) {
      console.error(
        `BusinessLinkModal: invalid or missing "mode" prop (got ${JSON.stringify(mode)}) — ` +
        `must be exactly one of "create" | "addMember" | "link".`
      );
    } else if (mode === "addMember" && !fixedBusiness) {
      console.error('BusinessLinkModal: mode="addMember" requires a fixedBusiness prop (the firm being added to).');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, fixedBusiness]);

  // Scroll the preselected row into view on open ("link" mode's own suggestion-banner
  // preselection — manual-testing report: the "Dipilih" chip was visible, but the actual
  // KONTAK/BISNIS list still sat scrolled to the top, so a preselected row far down either capped
  // section looked exactly like nothing had been selected there). Runs once, off the INITIAL
  // selection only — a later manual tick already happened via a real click on a row the user
  // could already see, so there's nothing to scroll to for that case.
  useEffect(() => {
    if (initialSelectedContactIds.length === 0 && initialSelectedBusinessIds.length === 0) return;
    const checked = sectionsRef.current?.querySelector('input[type="checkbox"]:checked');
    // Optional-chained on the CALL itself, not just the element — jsdom (this project's test
    // environment) doesn't implement `scrollIntoView` at all (not even as a no-op stub), unlike
    // a real browser, so `el?.scrollIntoView(...)` alone still throws "not a function" here.
    checked?.closest(".picker__row")?.scrollIntoView?.({ block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every non-internal contact except `contact` itself (`"link"` only — `contact` is always null
  // in "create"/"addMember") — including one already tied to a firm, which is filtered out of
  // PICKABILITY (see the render below) but never out of the list itself, per the "show it, don't
  // hide it" fix. A quick id -> Business lookup for the label, needed in every mode (an
  // already-linked KONTAK row's "Sudah di {firm}" label has nothing to do with which mode this
  // is — it's just naming an existing fact about that contact).
  const businessById = useMemo(() => new Map(businesses.map((b) => [b.id, b])), [businesses]);
  const contactCandidates = useMemo(
    () => contacts.filter((c) => (!contact || c.id !== contact.id) && !c.internal),
    [contacts, contact]
  );
  // Only ever rendered in "link" mode — computed unconditionally anyway (cheap, and keeps the
  // hook order/shape identical across modes), simply unused when the BISNIS section isn't shown.
  const businessCandidates = useMemo(
    () => businesses.filter((b) => !contact || b.id !== contact.businessId),
    [businesses, contact]
  );

  const filteredContacts = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return contactCandidates;
    const needlePhone = normalizePhone(query);
    return contactCandidates.filter((c) =>
      displayContact(c).toLowerCase().includes(needle) ||
      (needlePhone && normalizePhone(c.phone).includes(needlePhone))
    );
  }, [contactCandidates, query]);

  const filteredBusinesses = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return businessCandidates;
    return businessCandidates.filter((b) => displayBusiness(b).toLowerCase().includes(needle));
  }, [businessCandidates, query]);

  const toggleContact = (c) => {
    if (c.businessId) return; // unpickable — already tied to a firm, see the file header
    setSelectedContacts((s) => {
      const n = new Set(s);
      n.has(c.id) ? n.delete(c.id) : n.add(c.id);
      return n;
    });
  };
  const toggleBusiness = (id) => setSelectedBusinesses((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const selectedContactObjs = useMemo(
    () => contactCandidates.filter((c) => selectedContacts.has(c.id)),
    [contactCandidates, selectedContacts]
  );
  const selectedBusinessObjs = useMemo(
    () => businessCandidates.filter((b) => selectedBusinesses.has(b.id)),
    [businessCandidates, selectedBusinesses]
  );
  const totalSelectedCount = selectedContacts.size + selectedBusinesses.size;
  const involvedContactCount = (contact ? 1 : 0) + selectedContacts.size;
  // Per-mode minimum — an explicit branch, not one formula stretched to cover a genuinely
  // different rule: "addMember" only ever needs ONE contact (the firm itself is already fixed,
  // never ticked), where "create"/"link" need TWO real people unless an existing firm is ticked
  // ("link" only — "create" never renders a tickable firm, so `selectedBusinesses` stays empty
  // there and this reduces to a plain 2-contact minimum).
  const readyToProceed = mode === "addMember"
    ? selectedContacts.size >= 1
    : totalSelectedCount > 0 && (selectedBusinesses.size > 0 || involvedContactCount >= 2);

  const proceed = () => {
    if (!readyToProceed) return;
    const involvedBizIds = new Set([
      ...(contact?.businessId ? [contact.businessId] : []),
      ...(mode === "addMember" ? [fixedBusiness.id] : []),
      ...selectedContactObjs.map((c) => c.businessId).filter(Boolean),
      ...selectedBusinessObjs.map((b) => b.id),
    ]);
    if (involvedBizIds.size > 1) {
      setConflictError(
        mode === "addMember"
          ? `Salah satu kontak yang dipilih sudah tergabung di bisnis lain (bukan ` +
            `${displayBusiness(fixedBusiness)}) — tidak bisa ditautkan otomatis di sini. ` +
            `Batalkan centang kontak itu, atau lepaskan dulu dari bisnisnya yang sekarang.`
          : "Pilihan ini mengarah ke bisnis yang berbeda — tidak bisa digabung otomatis di sini. " +
            "Kurangi pilihan, atau tautkan satu per satu."
      );
      return;
    }
    setConflictError(null);
    const contactIds = [...(contact ? [contact.id] : []), ...selectedContactObjs.map((c) => c.id)];
    if (involvedBizIds.size === 1) {
      onConfirm({ contactIds, businessId: [...involvedBizIds][0] });
      return;
    }
    // Nobody involved has a business yet ("create"/"link" only — "addMember" can never reach
    // this, `fixedBusiness.id` always makes `involvedBizIds.size` >= 1) — the name is the user's
    // to state, never guessed from any contact's or firm's own name.
    setStep("name");
  };

  const submitName = () => {
    const name = businessName.trim();
    if (!name) return;
    onConfirm({ contactIds: [...(contact ? [contact.id] : []), ...selectedContactObjs.map((c) => c.id)], businessName: name });
  };

  const shownError = conflictError || error;

  if (!isValidProps) {
    return (
      <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
        <div className="modal-box" onMouseDown={(e) => e.stopPropagation()}>
          <div className="form-hint" style={{ color: "var(--red)" }}>
            BusinessLinkModal: prop "mode" wajib diisi dengan salah satu dari
            "create" | "addMember" | "link"{mode === "addMember" ? ", dan mode \"addMember\" wajib menyertakan fixedBusiness" : ""}.
          </div>
          <div className="modal-actions">
            <button type="button" className="btn" onClick={onClose}>Tutup</button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "name") {
    return (
      <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
        <div className="modal-box" onMouseDown={(e) => e.stopPropagation()}>
          <h2 className="modal-title">Nama Bisnis</h2>
          <div className="form-hint" style={{ marginBottom: "var(--gap-md)" }}>
            {selectedContactObjs.length + (contact ? 1 : 0)} kontak akan digabung menjadi satu
            bisnis. Belum ada yang tertaut ke bisnis sebelumnya — beri nama bisnis ini.
          </div>
          <div className="form-row">
            <label className="form-label" htmlFor="biz-link-nama">Nama Bisnis</label>
            <input id="biz-link-nama" className="form-input" autoFocus value={businessName}
              onChange={(e) => setBusinessName(e.target.value)} />
            {/* Disabled-control sweep (manual-testing report) — shared convention. */}
            {!businessName.trim() && <div className="form-hint" style={{ color: "var(--red)" }}>Wajib diisi.</div>}
          </div>
          {shownError && <div className="form-hint" style={{ color: "var(--red)" }}>{shownError}</div>}
          <div className="modal-actions">
            <button type="button" className="btn" onClick={() => setStep("pick")}>Kembali</button>
            <button type="button" className="btn btn-primary" disabled={!businessName.trim()} onClick={submitName}>
              Tautkan ({selectedContactObjs.length + (contact ? 1 : 0)})
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
      <div className="modal-box modal-box--wide" style={{ position: "relative" }} onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Tutup">×</button>
        <h2 className="modal-title" style={{ marginBottom: "var(--gap-sm)" }}>Tautkan ke Bisnis</h2>
        <div className="form-hint" style={{ marginBottom: "var(--gap-md)" }}>
          {mode === "link" ? (
            contact ? (
              "Cari bisnis yang sudah ada, atau kontak lain (Pusat maupun Cabang), yang sebenarnya perusahaan/toko yang sama."
            ) : (
              "Pilih bisnis yang sudah ada, atau kontak lain, yang sebenarnya perusahaan/toko yang sama."
            )
          ) : mode === "create" ? (
            "Pilih beberapa kontak (minimal dua) untuk digabung menjadi satu bisnis baru."
          ) : (
            "Pilih kontak untuk ditambahkan."
          )}
        </div>

        <div className="picker__search">
          <input className="form-input grow" autoFocus value={query}
            placeholder={mode === "link"
              ? "Cari nama bisnis, nama kontak, atau nomor telepon…"
              : "Cari nama kontak atau nomor telepon…"}
            onChange={(e) => setQuery(e.target.value)} />
        </div>

        {mode === "link" && contact && (
          <div className="kontak-section-label" style={{ marginBottom: "var(--gap-md)" }}>
            Menautkan kontak ini: <b>{displayContact(contact)}</b>
          </div>
        )}

        {/* "Dipilih" chips — ALWAYS visible above both scrollable sections, regardless of where
            a ticked row sits (or whether the render cap below has even pushed it out of view).
            A count with nothing visible to point at is the same shape as a value the user can't
            see — this is what makes a preselected candidate (from the suggestion banner) or a
            manually-ticked row far down a long list actually legible without scrolling.
            Deliberately holds ONLY things the user actually ticked — `contact` (`"link"`) and
            `fixedBusiness` (`"addMember"`) were never a tick, so neither ever appears here; each
            gets its own heading line instead, never a chip pretending it's removable. `"link"`'s
            own heading renders ABOVE this block (read order: who this is about, then what's been
            picked); `"addMember"`'s renders BELOW it, unmoved — two independent JSX blocks, not
            one shared position. */}
        {totalSelectedCount > 0 && (
          <div className="fitment-chips" style={{ marginBottom: "var(--gap-md)" }}>
            {selectedBusinessObjs.map((b) => (
              <span key={`biz-${b.id}`} className="fitment-chip">
                {displayBusiness(b)} (bisnis)
                <button type="button" className="fitment-chip__x" aria-label={`Batalkan ${displayBusiness(b)}`}
                  onClick={() => toggleBusiness(b.id)}>×</button>
              </span>
            ))}
            {selectedContactObjs.map((c) => (
              <span key={`contact-${c.id}`} className="fitment-chip">
                {c.name}
                <button type="button" className="fitment-chip__x" aria-label={`Batalkan ${c.name}`}
                  onClick={() => toggleContact(c)}>×</button>
              </span>
            ))}
          </div>
        )}

        {mode === "addMember" && (
          <div className="kontak-section-label" style={{ marginBottom: "var(--gap-md)" }}>
            Menambahkan anggota ke: <b>{displayBusiness(fixedBusiness)}</b>
          </div>
        )}

        <div ref={sectionsRef}>
        {mode === "link" && (
          <>
            <div className="kontak-section-label">Bisnis ({filteredBusinesses.length})</div>
            <div className="picker__list" style={{ maxHeight: 200, marginBottom: "var(--gap-lg)" }}>
              {filteredBusinesses.length === 0 ? (
                <div className="combobox__empty">Tidak ada bisnis yang cocok.</div>
              ) : (
                <>
                  {filteredBusinesses.slice(0, RENDER_CAP).map((b) => (
                    <div key={b.id} className="picker__row" onClick={() => toggleBusiness(b.id)}>
                      <input type="checkbox" className="picker__cb" checked={selectedBusinesses.has(b.id)}
                        onChange={() => toggleBusiness(b.id)} onClick={(e) => e.stopPropagation()} />
                      <div className="picker__name">{displayBusiness(b)}</div>
                      <div className="picker__meta">{businessRefCount({ contacts }, b.id)} entri</div>
                    </div>
                  ))}
                  {filteredBusinesses.length > RENDER_CAP && (
                    <div className="form-hint" style={{ padding: "8px 12px" }}>
                      Menampilkan {RENDER_CAP} dari {filteredBusinesses.length} — ketik untuk mempersempit.
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        <div className="kontak-section-label">Kontak ({filteredContacts.length})</div>
        <div className="picker__list" style={{ maxHeight: 200 }}>
          {filteredContacts.length === 0 ? (
            <div className="combobox__empty">Tidak ada kontak yang cocok.</div>
          ) : (
            <>
              {filteredContacts.slice(0, RENDER_CAP).map((c) => {
                const pickable = !c.businessId;
                const ownFirm = c.businessId ? businessById.get(c.businessId) : null;
                const ownFirmLabel = c.businessId ? (ownFirm ? displayBusiness(ownFirm) : "bisnis lain") : null;
                return (
                  <div key={c.id}
                    className={"picker__row" + (pickable ? "" : " picker__row--dim kontak-picker-row--unpickable")}
                    onClick={() => toggleContact(c)}>
                    <input type="checkbox" className="picker__cb" checked={pickable && selectedContacts.has(c.id)}
                      disabled={!pickable}
                      onChange={() => toggleContact(c)} onClick={(e) => e.stopPropagation()} />
                    <div className="picker__name">
                      {c.name}
                      <span className="chip chip--muted" style={{ marginLeft: "var(--gap-sm)" }}>{LIST_LABEL[c.list] || c.list}</span>
                      {c.deactivated && <span className="chip chip--muted" style={{ marginLeft: "var(--gap-sm)" }}>Nonaktif</span>}
                    </div>
                    <div className="picker__meta">
                      {pickable ? (c.phone || "—") : `Sudah di ${ownFirmLabel}`}
                    </div>
                  </div>
                );
              })}
              {filteredContacts.length > RENDER_CAP && (
                <div className="form-hint" style={{ padding: "8px 12px" }}>
                  Menampilkan {RENDER_CAP} dari {filteredContacts.length} — ketik untuk mempersempit.
                </div>
              )}
            </>
          )}
        </div>
        </div>

        {shownError && <div className="form-hint" style={{ color: "var(--red)", marginTop: "var(--gap-md)" }}>{shownError}</div>}
        {/* Disabled-control sweep (manual-testing report): the live "(N)" count in the button
            label already self-explains the N=0 case (nothing picked yet), but NOT this one — a
            user who has picked exactly one contact sees a non-zero count and a still-disabled
            button with nothing saying a lone contact isn't enough on its own. Only fires for
            "create"/"link" (addMember's own minimum is 1, already satisfied the instant anything
            is picked) and only once something's actually been picked, so it never fights the
            self-evident N=0 case. */}
        {mode !== "addMember" && !readyToProceed && totalSelectedCount > 0 && (
          <div className="form-hint" style={{ color: "var(--red)", marginTop: "var(--gap-md)" }}>
            Pilih minimal 2 kontak, atau 1 bisnis yang sudah ada, untuk ditautkan.
          </div>
        )}

        <div className="picker__foot">
          <div className="picker__count">{totalSelectedCount} dipilih</div>
          <div style={{ display: "flex", gap: "var(--gap-md)" }}>
            <button type="button" className="btn" onClick={onClose}>Batal</button>
            <button type="button" className="btn btn-primary" disabled={!readyToProceed} onClick={proceed}>
              Tautkan ({totalSelectedCount})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
